/**
 * Portal landing page at `/c/${clientSlug}`. Renders inside `TenantChrome`
 * (parent route). Two paths:
 *   1. Signed-in (MSAL active account on the portal's tenant): auto-redirect
 *      to `/c/${slug}/sharepoint` so the visitor immediately sees the real
 *      data path they just authenticated for.
 *   2. Signed-out: centered branded splash with a single primary CTA
 *      ("Sign in with Microsoft", Microsoft brand styling) plus a secondary
 *      "Try the demo workspace" affordance that routes to the Win95 desktop
 *      with seeded fixtures.
 *
 * No placeholder copy. The two-card scaffold from F16.B is replaced —
 * lawyer-prospects need a clean front door, not "wired in F16.C" notes.
 */

import { useEffect } from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useTenantBranding } from '../../../lib/tenant/TenantBrandingContext';
import { useOptionalPortalMsal } from '../../../lib/tenant/MsalInstanceProvider';
import { TenantLogo } from '../../../components/branding/TenantLogo';
import { MicrosoftSignInButton } from '../../../components/branding/MicrosoftSignInButton';
import { testIds } from '../../../lib/testIds';

function PortalLanding() {
  const { branding, slug } = useTenantBranding();
  const msal = useOptionalPortalMsal();
  const navigate = useNavigate();
  const search = useSearch({ from: '/c/$clientSlug/' });

  const account = msal?.account ?? null;

  // Path 1: signed-in users go straight to the SharePoint surface so they
  // see the live data they just authenticated for. Without this redirect
  // the user signs in, the URL stays at /c/$slug, and they have no obvious
  // indication that anything changed.
  // F18.demo.4.2 — forward any ?panel= search param to /desktop so the
  // post-onboarding "bring-your-own" buttons survive the redirect.
  useEffect(() => {
    if (account !== null) {
      void navigate({
        to: '/c/$clientSlug/desktop',
        params: { clientSlug: slug },
        search: search.panel ? { panel: search.panel } : undefined,
        replace: true,
      });
    }
  }, [account, navigate, slug, search.panel]);

  // Path 1b: even when MSAL is not signed in, if the URL carries a
  // ?panel= value the user is intent on landing in a window (typically
  // from the post-onboarding "Connect my SharePoint" / "Import
  // Obsidian" buttons in demo mode, where MSAL is never required).
  // Forward to /desktop unconditionally so PanelAutoOpener fires.
  // Otherwise demo users would see the Microsoft sign-in tile and the
  // panel query string would be silently dropped.
  useEffect(() => {
    if (account === null && search.panel) {
      void navigate({
        to: '/c/$clientSlug/desktop',
        params: { clientSlug: slug },
        search: { panel: search.panel },
        replace: true,
      });
    }
  }, [account, navigate, slug, search.panel]);

  if (account !== null) {
    return (
      <section
        data-testid={testIds.portal.landingTile}
        className="max-w-3xl mx-auto px-6 py-12 text-center text-black"
      >
        <p className="text-sm text-gray-700">Loading your workspace&hellip;</p>
      </section>
    );
  }

  const handleSignIn = (): void => {
    if (!msal) {return;}
    void msal.signIn();
  };

  const handleTryDemo = (): void => {
    void navigate({
      to: '/c/$clientSlug/desktop',
      params: { clientSlug: slug },
      search: search.panel ? { panel: search.panel } : undefined,
    });
  };

  return (
    <section
      data-testid={testIds.portal.landingTile}
      className="max-w-3xl mx-auto px-6 py-16 flex flex-col items-center gap-8 text-center text-black"
    >
      <TenantLogo size="landing" />

      <div className="flex flex-col gap-3 items-center">
        <h1 className="text-4xl font-semibold tracking-tight">
          {branding.displayName}
        </h1>
        <p
          data-testid={testIds.portal.landingValueProp}
          className="text-lg text-gray-800 max-w-xl"
        >
          AI-native legal workspace. Skills that draft and review against
          your firm&rsquo;s real matters &mdash; with citations, audit
          trails, and SharePoint as the system of record.
        </p>
      </div>

      <div className="flex flex-col gap-4 items-center mt-4">
        <MicrosoftSignInButton
          data-testid={testIds.portal.landingSignInPrimary}
          onClick={handleSignIn}
          disabled={!msal || !msal.ready}
          label="Sign in with Microsoft"
          subLabel={`Connect ${branding.displayName}'s SharePoint`}
        />
        <button
          type="button"
          data-testid={testIds.portal.landingDemoPrimary}
          onClick={handleTryDemo}
          className="text-sm text-gray-700 underline hover:text-black"
        >
          Or try the demo workspace first &rarr;
        </button>
      </div>
    </section>
  );
}

// Exported so TanStack's generated routeTree.gen.ts can name the
// inferred search-param type on the Route's exported binding.
export interface PortalLandingSearch {
  panel?: string;
}

export const Route = createFileRoute('/c/$clientSlug/')({
  component: PortalLanding,
  // F18.demo.4.2 — accept ?panel= so the post-onboarding "bring-your-own"
  // buttons can deeplink to the right window inside the desktop.
  validateSearch: (search: Record<string, unknown>): PortalLandingSearch => ({
    panel: typeof search.panel === 'string' ? search.panel : undefined,
  }),
});
